// CalendarView.jsx
import React, { useEffect, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import { decryptToken } from "../../../../Utlis/Encryption.js";
import { BASE_URL } from "../../../../components/BaseUrl.js";
import TruncatedCell from "../../../../components/TruncatedCell.js";
import {useTranslation} from "react-i18next";
import { convertUTCToLocalDateTime } from "../../../../components/dateTimeUtils";

const localizer = momentLocalizer(moment);

const formatDateTime = (dateString) => {
  if (!dateString) return "-";
  const localDateTime = convertUTCToLocalDateTime(dateString, { locale: "en-GB" });
  return localDateTime === "—" ? "-" : localDateTime;
};

const formatTaskTimeRange = (startAt, endAt) => {
  const start = formatDateTime(startAt);
  const end = formatDateTime(endAt);
  if (start === "-" && end === "-") return "-";
  if (start === "-") return end;
  if (end === "-") return start;
  return `${start} - ${end}`;
};

const CalendarView = ({ onSelectTask,
  selectedTaskId, onClearFilter,
  appliedFilters = {},
}) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const rawEncryptedToken = localStorage.getItem("token");
  const token = rawEncryptedToken ? decryptToken(rawEncryptedToken) : null;

  useEffect(() => {
    const fetchTasks = async () => {
      try {
        const res = await fetch(
          `${BASE_URL}/task-management/worker/tasks?page=1&page_size=50&show_history=true`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await res.json();

        if (!data.error) {
          const mappedEvents = data.data.tasks.map((task) => ({
            id: task.id,
            title: task.title,
            start: moment.utc(task.start_at).local().toDate(),
            end: moment.utc(task.end_at).local().toDate(),
            timeRange: formatTaskTimeRange(task.start_at, task.end_at),
            status: task.status,
            worker: task.assigned_by_name || "—",
            location: task.location_address,
          }));
          setEvents(mappedEvents);
        }
      } catch (err) {
        console.error("❌ Error fetching tasks:", err);
      } finally {
        setLoading(false);
      }
    };

    if (token) fetchTasks();
  }, [token]);

  const eventStyleGetter = (event) => {
    let backgroundColor = "#90caf9";
    if (event.status === "completed") backgroundColor = "#a5d6a7";
    if (event.status === "assigned") backgroundColor = "#64b5f6";
    if (event.status === "cancelled") backgroundColor = "#ef9a9a";
    if (event.status === "in_progress") backgroundColor = "#ffcc80";
    if (event.status === "not_done") backgroundColor = "#b0bec5";

    return {
      style: {
        backgroundColor,
        color: "white",
        borderRadius: "6px",
        border: "0px",
        display: "block",
        paddingLeft: "4px",
      },
    };
  };
  const visibleEvents = selectedTaskId
    ? events.filter((e) => e.id === selectedTaskId)
    : events;

  const filterByDrawer = (event) => {
    const { status, startDate, endDate } = appliedFilters || {};

    if (status && event.status !== status) return false;

    if (startDate) {
      const startBoundary = new Date(`${startDate}T00:00:00`);
      if (event.start < startBoundary) return false;
    }

    if (endDate) {
      const endBoundary = new Date(`${endDate}T23:59:59`);
      if (event.end > endBoundary) return false;
    }

    return true;
  };

  const filteredEvents = visibleEvents.filter(filterByDrawer);



    const {t} = useTranslation();
  return (
    <Box sx={{ position: "relative", height: "80vh", p: 2, background: "#fff", borderRadius: "8px" }}>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {selectedTaskId && (
            <Box
              sx={{
                position: "absolute",
                top: 12,
                left: 12,
                zIndex: 10,
                background: "red",
                color: "white",
                borderRadius: "50%",
                cursor: "pointer",
                boxShadow: "0 0 6px rgba(0,0,0,0.2)",
                width: 30,
                height: 30,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 18,
                fontWeight: 600,
              }}
              onClick={onClearFilter}
            >
              ×
            </Box>
          )}

          <Calendar
            onSelectEvent={(event) => onSelectTask(event.id)} // send task id to parent
            localizer={localizer}
            events={filteredEvents}
            defaultView="month"
            views={["day", "week", "month"]}
            startAccessor="start"
            endAccessor="end"
            style={{ height: "100%" }}
            eventPropGetter={eventStyleGetter}
                        messages={{
  today: t("calendar.today"),
  previous: t("calendar.back"),
  next: t("calendar.next"),
  month: t("calendar.month"),
  week: t("calendar.week"),
  day: t("calendar.day"),
  agenda: t("calendar.year")
}}
            components={{
              event: ({ event }) => (
                <Box
                  sx={{
                    lineHeight: 1.25,
                    overflow: "hidden",
                    width: "100%",
                  }}
                  title={`${event.title}\n${event.timeRange}`}
                >
                  <TruncatedCell
                    text={event.title}
                    maxLength={22}
                    sx={{ color: "white", fontSize: 12, maxWidth: "100%" }}
                  />
                  <Box
                    component="span"
                    sx={{
                      display: "block",
                      fontSize: 10,
                      color: "rgba(255,255,255,0.95)",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      fontFamily: "Poppins, sans-serif",
                      mt: 0.25,
                    }}
                  >
                    {event.timeRange}
                  </Box>
                </Box>
              ),
            }}
          />
        </>
      )}
    </Box>
  );
};

export default CalendarView;
